// src/features/locations/PhotoPanel.jsx
//
// Slice 11b: interactive photo display for the LocationDetail header.
// Shows the location image (when set) or a placeholder, with admin-only
// Upload / Replace / Delete affordances.
//
// Slice L6 update: header now hosts a Photo/Map segmented toggle (P1
// pattern). When set to "Map" the photo frame is replaced by a small
// read-only map centered on the location's lat/lng.
//
// The map surface shares the portfolio map's keyless MapLibre/OpenFreeMap
// foundation without embedding its sidebar or cluster UI.
//
// Edge case — missing coordinates: when location.lat or location.lng
// is null/non-finite the Map toggle is rendered but DISABLED (with
// `aria-disabled` + visual dim). Chosen over hidden so the toggle
// strip width stays stable across rows and so the user understands
// the affordance exists but the data is incomplete. Hidden would
// have been less informative.
//
// Toggle state is local to this panel; switching to a sibling tab
// (Lease/Cost/Utilization) unmounts PhotoPanel and the toggle resets
// to "photo" on remount. Brief allowed either approach — local was
// the smaller diff.
//
// Server contract (slice 11a, src/data/CONTRACT.md):
//   POST   /api/locations/:id/photo   multipart, multer 5MB cap,
//                                     JPEG/PNG/WebP allowlist
//   DELETE /api/locations/:id/photo   no-op when image_url is already null
//
// Client-side validation mirrors the server allowlist + size cap so the
// user sees the rejection immediately rather than waiting for a 400. The
// banner copy on a server-side rejection is rendered verbatim — that's
// the same rule LeasePanel uses for the active-lease conflict.

import React, { useRef, useState, useEffect } from "react";
import {
  uploadLocationPhoto,
  deleteLocationPhoto,
  ValidationError,
} from "../../data/locationsClient.js";
import { T } from "../../app/tokens.js";
import { Tooltip } from "../../components/Tooltip.jsx";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";
import { HealthBadge } from "../../components/HealthBadge.jsx";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { OfficeMapCanvas, preciseOfficePosition } from "./OfficeMapFoundation.jsx";

const PRIMARY_BUTTON   = { background: T.blue, color: T.white, border: "none", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 18px", cursor: "pointer" };
const SECONDARY_BUTTON = { background: T.white, color: T.mid, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold, padding: "6px 14px", cursor: "pointer" };
const DANGER_BUTTON    = { background: T.red, color: T.white, border: "none", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 14px", cursor: "pointer" };

const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES     = 5 * 1024 * 1024;

// PREMISES_SLOT_HEIGHT — reserved height for the premises/locationId slot
// below the photo frame. The tallest branch (premises present) occupies
// ~24 px (padding 4+8 + fontSize-10 content); 28 px gives a comfortable
// floor. The slot always renders in the DOM regardless of whether premises
// is populated, so the top-row card height never shifts between locations
// that differ in suite presence.
const PREMISES_SLOT_HEIGHT = 28;

// HUB-LOCATION-S1: dark slate-navy for the Hub badge. Now routed through T.hubChipText.
const HUB_SLATE = T.hubChipText;

// Photo, map and placeholder share a fixed frame so the neighboring capacity
// pane cannot stretch or squeeze the image when its content changes.
const PROPERTY_FRAME_HEIGHT = 210;

const FRAME = {
  position: "relative",
  width: "100%",
  flex: "0 0 auto",
  height: PROPERTY_FRAME_HEIGHT,
  borderRadius: 8,
  overflow: "hidden",
  background: T.bg,
  border: `1px solid ${T.border}`,
};

// OFFICE-MAP-PIN-PRECISION: accuracy types that are NOT trustworthy enough
// for a precise building pin. Map toggle is disabled when accuracy is one of
// these, even when coordinates exist.
const FLAGGED_ACCURACY_TYPES = new Set(["GEOMETRIC_CENTER", "APPROXIMATE"]);

// L6 — P1 segmented control segment. Mirrors PortfolioView.ToggleButton
// (per-segment borders removed; track on the parent container; active
// = white + cardShadow). Inlined here so PhotoPanel stays self-
// contained — promotion to a shared component can wait until a third
// caller materializes.
function ToggleSeg({ label, active, onClick, disabled, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      data-testid={testid}
      title={title}
      style={{
        padding: "4px 12px",
        fontSize: T.fsXs5,
        fontWeight: active ? 600 : 500,
        color: disabled ? T.light : (active ? T.dark : T.mid),
        background: active ? T.white : "transparent",
        border: "none",
        borderRadius: 5,
        boxShadow: active ? T.cardShadow : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background 0.12s, color 0.12s, box-shadow 0.12s",
      }}
    >
      {label}
    </button>
  );
}

/**
 * @param {object}       props
 * @param {object}       props.location   Full location row (uses .id, .imageUrl, .lat, .lng).
 * @param {boolean}      props.isAdmin    Gates Upload / Replace / Delete buttons.
 * @param {boolean}      [props.editMode] LP1.3 Gate 1 — when false (default),
 *   admin Replace/Delete affordances are hidden so the photo surface
 *   stays clean. Gate 2 wires this to LocationDetail's cross-tab
 *   editMode state so Replace/Delete only appear while editing.
 * @param {() => void}   props.onChanged  Called after a successful write so the
 *   parent can refetch — same contract as LeasePanel.onChanged.
 * @param {string|null}  [props.address]  C2 — full address string, rendered in
 *   the below-photo row with Maps ↗ and directions links. Omitted when null.
 * @param {string|null}  [props.health]     Health value for the card header pills.
 * @param {boolean}      [props.showHealth] OVERVIEW-ALIGN — when false the HealthBadge
 *   is hidden from the pill pair. Defaults to true for backward compatibility.
 * @param {boolean}      [props.canWrite]   True when pills should be interactive.
 * @param {Function}     [props.onHealthChange] Called after a health change.
 * @param {string|null}  [props.locationId] OVERVIEW-ALIGN — when provided, renders
 *   the location ID in small text beneath the truncated address.
 * @param {string|null}  [props.premises]  PREMISES-DESC — when provided and non-empty,
 *   renders the premises description beneath the Location ID: subtle top divider,
 *   building icon, "PREMISES" eyebrow, and value text. Hidden when null or empty.
 */
export function PhotoPanel({ location, isAdmin, editMode = false, onChanged, address,
  health, showHealth = true, canWrite = false, onHealthChange,
  locationId = null, premises = null }) {
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);
  const [confirming, setConf] = useState(false);
  // L6: 'photo' | 'map'. Local state; resets on tab-switch unmount.
  const [view, setView]       = useState("photo");
  const addressIntent = useHoverIntent();
  const addrTipOpen = addressIntent.open;
  // LSE-PHOTO-CARD-ADDRESS-TOOLTIP-REFINE: truncation-detected tooltip +
  // Copy icon. isTruncated is updated by ResizeObserver so it reacts to
  // grid-column width changes (e.g. window resize, sidebar toggle).
  const [isTruncated, setIsTruncated] = useState(false);
  const [copied,      setCopied]      = useState(false);
  const addrTextRef  = useRef(null);
  const fileInputRef = useRef(null);
  const copyTimerRef = useRef(null);
  const compactMapRef = useRef(null);

  // Cancel any pending copy-feedback timer on unmount so we never call
  // setCopied(false) on a dead component.
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const hasPhoto = Boolean(location.imageUrl);

  // OFFICE-MAP-PIN-PRECISION: prefer geocoded geo_lat/geo_lng; fall back to
  // legacy lat/lng only when geo columns are null (not yet backfilled).
  // Map toggle is disabled when coordinates are missing OR when the geocoded
  // accuracy is flagged (GEOMETRIC_CENTER / APPROXIMATE) — a wrong building
  // pin is worse than a disabled toggle.
  const isAccuracyFlagged =
    location.geoLat != null &&
    FLAGGED_ACCURACY_TYPES.has(location.geoAccuracy);
  const position = preciseOfficePosition(location);
  const hasCoords = position != null;

  useEffect(() => {
    if (!hasCoords) setView("photo");
  }, [location.id, hasCoords]);

  // Detect whether the address text is overflowing its container.
  // Re-runs whenever the column width changes (ResizeObserver) or the
  // address prop changes. Guards both the tooltip and the Copy icon.
  useEffect(() => {
    const el = addrTextRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [address]);

  const openPicker = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice in a row still fires
    // a change event (browsers suppress duplicate selections otherwise).
    e.target.value = "";
    if (!file) return;

    // Client-side guards. The server enforces the same rules — the wire
    // call is the source of truth — but blocking the obvious bad cases
    // here saves a 5MB upload that's destined to 400.
    if (!ALLOWED_TYPES.has(file.type)) {
      setError(new ValidationError(
        `Unsupported file type: ${file.type || "unknown"}. Use JPEG, PNG, or WebP.`,
        { field: "photo" },
      ));
      return;
    }
    if (file.size > MAX_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      setError(new ValidationError(
        `File too large (${mb} MB). Maximum is 5 MB.`,
        { field: "photo" },
      ));
      return;
    }

    setBusy(true);
    try {
      log.ui("PhotoPanel: uploading", { id: location.id, size: file.size, type: file.type });
      await uploadLocationPhoto(location.id, file);
      setError(null);
      showToast("Photo uploaded.", { type: "success" });
      onChanged?.();
    } catch (err) {
      // Surface the verbatim server message — no rephrasing. The server's
      // 400 messages already explain "type" vs "size" failures cleanly.
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      log.ui("PhotoPanel: deleting", { id: location.id });
      await deleteLocationPhoto(location.id);
      setError(null);
      setConf(false);
      showToast("Photo deleted.", { type: "success" });
      onChanged?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ borderRadius: 10, height: "100%", display: "flex", flexDirection: "column" }}>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* LP1 Gate 1 — sub-header bar inside the outer card. Mirrors the
          legacy DetailCard pattern (Ripple Workplace App.html:3255):
          11px×18px padding, bottom border, gap 9, with a 3×16 T.amber
          accent bar to the left of the title. Title text follows the
          legacy "{market} · {alias} Office" / "{market} Office" form.
          The Photo/Map toggle (L6) stays right-aligned via marginLeft
          auto on the toggle wrapper — its testids and behavior are
          unchanged. */}
      {/* C7 — sub-header min-height matches the natural height of this
          card's sub-header (toggle + accent + title), so the Location
          Details sub-header next door matches and content below both
          (photo image / Market row) starts at the same vertical
          offset. If toggle dimensions change, this value updates
          accordingly. Kept symmetric with LocationDetailsPanel's
          sub-header — same padding, same border, same minHeight, same
          marginBottom — there is no shared style constant; future DRY
          refactor would extract a `panelSubHeader` style. */}
      <div
        style={{
          padding: "8px 20px",
          display: "flex",
          alignItems: "center",
          gap: 9,
          minHeight: 40,
          marginBottom: 0,
        }}
        data-testid="photo-panel-header"
      >
        <PaneHeading
          barProps={{ "data-testid": "photo-panel-accent" }}
          style={{ marginBottom: 0, minWidth: 0, overflow: "hidden" }}
          labelStyle={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
        >
          Property
        </PaneHeading>
        {/* OFFICE-OVERVIEW-MOVE-STAGE-TO-HEADER: Stage pill relocated to Zone 1
            header (location-header-identity-strip). Property pane heading now
            shows only the Photo/Map toggle (and Health when showHealth=true). */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {showHealth && (
            <div
              data-testid="identity-pill-pair"
              style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 5 }}
            >
              <span style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.light, letterSpacing: "0.08em", textTransform: "uppercase" }}>HEALTH</span>
              <HealthBadge health={health} canWrite={canWrite} onChange={canWrite ? onHealthChange : undefined} />
            </div>
          )}
          <div
            role="group"
            aria-label="Photo or map view"
            data-testid="photo-map-toggle"
            style={{
              display: "inline-flex",
              gap: 2,
              padding: 2,
              borderRadius: 7,
              background: T.lightGray,
            }}
          >
            <ToggleSeg
              label="Photo"
              active={view === "photo"}
              onClick={() => setView("photo")}
              testid="photo-map-toggle-photo"
            />
            <ToggleSeg
              label="Map"
              active={view === "map"}
              onClick={() => hasCoords && setView("map")}
              disabled={!hasCoords}
              testid="photo-map-toggle-map"
              title={
                isAccuracyFlagged
                  ? "Location approximate - map disabled"
                  : !hasCoords
                  ? "No coordinates on file"
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* Keep the frame and metadata together even when the outer card stretches. */}
      <div data-testid="photo-frame-wrap" style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", padding: "12px 20px 8px" }}>
      <div style={FRAME} data-testid="photo-frame">
        {view === "map" && hasCoords ? (
          <div
            data-testid="photo-mini-map"
            style={{ position: "absolute", inset: 0 }}
          >
            <OfficeMapCanvas
              features={[{
                key: location.id,
                coordinates: position,
                locations: [location],
                color: T.blue,
                severity: 5,
              }]}
              mapRef={compactMapRef}
              center={position}
              zoom={15}
              cluster={false}
              testId="photo-mini-map-canvas"
              ariaLabel={`Map of ${location.market || location.id}`}
            />
          </div>
        ) : hasPhoto ? (
          <img
            src={location.imageUrl}
            alt={`${location.market || location.id} photo`}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "100%", height: "100%",
            color: T.light, fontSize: T.fsBase, fontWeight: T.fwSemibold,
            background: `repeating-linear-gradient(45deg, ${T.bg} 0 8px, ${T.card} 8px 16px)`,
          }}>
            No photo on file
          </div>
        )}

        {/* Admin overlay. Editor / viewer never see these. Hidden in
            map view so the upload buttons don't sit over Leaflet.
            LP1.3 Gate 1 — also gated on editMode so the affordances
            only surface while LocationDetail is in edit mode (Gate 2
            wires the prop). Default editMode=false keeps the photo
            surface clean for the read-only landing. */}
        {isAdmin && view === "photo" && editMode && (
          <div style={{
            position: "absolute", top: 8, right: 8,
            display: "flex", gap: 6,
          }}>
            {hasPhoto ? (
              <>
                <button
                  type="button"
                  onClick={openPicker}
                  disabled={busy}
                  style={{ ...PRIMARY_BUTTON, opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer", boxShadow: T.shadowButton }}
                >
                  {busy ? "Uploading…" : "Replace"}
                </button>
                <button
                  type="button"
                  onClick={() => setConf(true)}
                  disabled={busy}
                  style={{ ...DANGER_BUTTON, opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer", boxShadow: T.shadowButton }}
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openPicker}
                disabled={busy}
                style={{ ...PRIMARY_BUTTON, opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer", boxShadow: T.shadowButton }}
              >
                {busy ? "Uploading…" : "Upload photo"}
              </button>
            )}
          </div>
        )}
      </div>
      </div>{/* end LD1 padding wrapper */}

      {/* PHOTO-CARD-REFINE: Below-photo row.
          Left: address (truncated) + Copy + Maps ↗.
          Right: Stage control (moved from card header). */}
      <div
        data-testid="photo-below-row"
        style={{
          padding: "6px 20px 3px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {address && (
          <div
            data-testid="location-address"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              fontSize: T.fsBase,
              color: T.mid,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {/* Address text — truncates with ellipsis. Tooltip and Copy icon
                appear only when isTruncated (scrollWidth > clientWidth),
                detected by ResizeObserver so they respond to column-width
                changes. */}
            <span
              style={{ position: "relative", flex: "1 1 0", minWidth: 0, cursor: isTruncated ? "help" : "default" }}
              {...addressIntent.triggerProps}
              tabIndex={isTruncated ? 0 : undefined}
            >
              <span
                ref={addrTextRef}
                data-testid="location-address-text"
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {address}
              </span>
              {isTruncated && addrTipOpen && (
                <Tooltip
                  data-testid="location-address-tooltip"
                  style={{
                    left: 0,
                    bottom: "calc(100% + 4px)",
                    zIndex: 10,
                    whiteSpace: "normal",
                    maxWidth: 300,
                    wordBreak: "break-word",
                  }}
                >
                  {address}
                </Tooltip>
              )}
            </span>
            {/* Copy icon — only when address is truncated. Swaps to a
                checkmark for 1.5 s after a successful clipboard write. */}
            {isTruncated && (
              <button
                data-testid="location-address-copy"
                title="Copy address"
                onClick={() => {
                  navigator.clipboard.writeText(address)
                    .then(() => {
                      setCopied(true);
                      copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
                    })
                    .catch((err) => log.warn("[Warn] Clipboard write failed", { err }));
                }}
                style={{
                  flex: "0 0 auto",
                  background: "none",
                  border: "none",
                  padding: "0 2px",
                  cursor: "pointer",
                  color: T.mid,
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 0,
                }}
              >
                {copied ? (
                  <svg
                    data-testid="location-address-copy-check"
                    viewBox="0 0 16 16" width="12" height="12"
                    fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="2,8 6,12 14,4" />
                  </svg>
                ) : (
                  <svg
                    data-testid="location-address-copy-icon"
                    viewBox="0 0 16 16" width="12" height="12"
                    fill="none" stroke="currentColor"
                    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <rect x="5" y="1" width="9" height="13" rx="1" />
                    <path d="M5 3H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2" />
                  </svg>
                )}
              </button>
            )}
            {/* Maps ↗ link — always visible; href uses full address, not
                the possibly-truncated display text. */}
            <a
              data-testid="location-address-maps-link"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: "0 0 auto", color: T.blue, textDecoration: "none" }}
            >
              ↗
            </a>

          </div>
        )}
      </div>
      {/* LOCATION-CARD-HEIGHT-LOCK: premises slot always renders (even when
          premises is blank) so the card height is invariant across all
          locations. When premises is null the container is empty but retains
          PREMISES_SLOT_HEIGHT. locationId sits right-aligned inside the same
          container regardless of premises presence. */}
      <div
        data-testid="photo-below-meta"
        style={{ minHeight: PREMISES_SLOT_HEIGHT, flexShrink: 0 }}
      >
        <div
          data-testid="premises-line"
          style={{
            padding: "4px 20px 8px",
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            minHeight: PREMISES_SLOT_HEIGHT,
          }}
        >
          {premises && (
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <div
                data-testid="premises-eyebrow"
                style={{
                  fontSize: T.fsXxs,
                  color: T.light,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  lineHeight: 1.2,
                }}
              >
                PREMISES:
              </div>
              <div
                data-testid="premises-value"
                style={{ fontSize: T.fsXxs, color: T.mid, lineHeight: 1.4 }}
              >
                {premises}
              </div>
            </div>
          )}
          {locationId && (
            <div
              data-testid="location-id-sub"
              style={{ fontSize: T.fsXxs, color: T.light, marginLeft: "auto" }}
            >
              {locationId}
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input. accept= drives the OS picker filter; we still
          re-validate type in onFile because the OS picker is advisory. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        style={{ display: "none" }}
        data-testid="photo-file-input"
      />

      {/* Delete confirm. Inline (not a modal) so it works in the same
          frame as the existing inline confirms (CommentaryPanel,
          CandidatesPanel) — no portal, no focus-trap edge cases. */}
      {confirming && (
        <div
          role="alertdialog"
          aria-label="Delete photo confirmation"
          style={{
            background: T.card,
            border: `1px solid ${T.red}`,
            borderRadius: 8,
            padding: 12,
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, fontSize: T.fsBase, color: T.dark }}>
            Delete this photo? It cannot be undone.
          </div>
          <button className="wos-btn" onClick={onDelete} disabled={busy}
            style={{ ...DANGER_BUTTON, opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer" }}>
            {busy ? "Deleting…" : "Confirm delete"}
          </button>
          <button className="wos-btn" onClick={() => setConf(false)} disabled={busy} style={SECONDARY_BUTTON}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
